<template>
  <div class="bills-table-item">
    <transaction-table-item
      ref="transactionItem"
      :transaction="transaction"
      :details-blocks="transaction.TRANSACTION_DETAIL_BLOCKS"
      @actionFired="handleActionFired"
    >
      <template
        #shortInfo="{
          hideActionsMenu,
          toggleActionsMenu,
          isShowActionsMenu,
          makeAction,
          actionsPending,
        }"
      >
        <div class="transaction-table-item__col transaction-table-item__date">
          <div class="transaction-table-item__date-main">
            {{ transaction.transactionDate }}
          </div>
          <div class="transaction-table-item__date-time">
            {{ transaction.transactionTime }}
          </div>
        </div>
        <div class="transaction-table-item__col transaction-table-item__pt-id">
          {{ transaction.partnerTransactionId }}
        </div>
        <div class="transaction-table-item__col transaction-table-item__op-id">
          {{ transaction.operationId }}
        </div>
        <div class="transaction-table-item__col transaction-table-item__number">
          {{ transaction.orderNumber }}
        </div>
        <div
          class="transaction-table-item__col transaction-table-item__phone-email"
        >
          {{ transaction.userPhoneEmail }}
        </div>
        <div
          class="transaction-table-item__col transaction-table-item__recipient"
        >
          {{ transaction.userFullName }}
        </div>
        <div class="transaction-table-item__col transaction-table-item__amount">
          {{ transaction.transactionAmount }}
          {{ transaction.transactionCurrency }}
        </div>
        <div class="transaction-table-item__col transaction-table-item__status">
          <transaction-status
            :status-id="transaction.operationStatus"
          ></transaction-status>
        </div>
        <div
          class="transaction-table-item__col transaction-table-item__actions"
          v-click-outside="hideActionsMenu"
        >
          <div
            class="transaction-table-item__actions-btn"
            @click="toggleActionsMenu"
          >
            <svg
              width="20"
              height="4"
              viewBox="0 0 20 4"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle cx="2" cy="2" r="2" fill="#B9C4D8" />
              <circle cx="18" cy="2" r="2" fill="#B9C4D8" />
              <circle cx="10" cy="2" r="2" fill="#B9C4D8" />
            </svg>
          </div>
          <div
            class="transaction-table-item__actions-menu"
            v-if="isShowActionsMenu"
          >
            <template v-if="itemActions.length > 0">
              <template v-for="action in itemActions">
                <require-access
                  v-if="action.accessControl"
                  :action="action.accessControl.action"
                  :data="action.accessControl.data"
                >
                  <div
                    class="transaction-table-item__actions-menu-item"
                    :class="{ disabled: actionsPending.includes(action.id) }"
                    @click.stop="makeAction(action)"
                  >
                    {{ action.title }}
                  </div>
                </require-access>
                <div
                  class="transaction-table-item__actions-menu-item"
                  :class="{ disabled: actionsPending.includes(action.id) }"
                  @click.stop="makeAction(action)"
                  v-else
                >
                  {{ action.title }}
                </div>
              </template>
            </template>
            <template v-else>
              <div class="transaction-table-item__actions-no-actions">
                {{ $t('transaction-actions.no-available-actions') }}
              </div>
            </template>
          </div>
        </div>
      </template>
    </transaction-table-item>
    <share-bill-link-popup
      ref="shareBillLinkPopup"
      :bill="bill"
      :currency="transaction.transactionCurrency"
    ></share-bill-link-popup>
    <transaction-receipt-popup
      :transaction="transaction"
      :pdf-blob="transactionReceiptBlob"
      ref="transactionReceiptPopup"
    ></transaction-receipt-popup>
  </div>
</template>

<script>
import TransactionTableItem from '@vue-app/components/company/transactions/table/TransactionsTableItem.vue'
import { Transaction } from '@js-app/entities/transaction/Transaction'
import TransactionStatus from '@vue-app/components/transaction/TransactionStatus.vue'
import { CompaniesApi } from '@js-app/office/api/companies-api'
import TransactionReceiptPopup from '@vue-app/components/company/transactions/popup/TransactionReceiptPopup.vue'
import { REFUND } from '@js-app/const/operation-types-ids'
import { RouteHelper } from '@js-app/services/helpers/routes/routes-helper'
import { mapState } from 'vuex'
import { SupportService } from '@js-app/services/support/support-service'
import RequireAccess from '@vue-app/components/base/role-based/RequireAccess.vue'
import ShareBillLinkPopup from '@vue-app/components/company/bills/popup/ShareBillLinkPopup.vue'
import { Bill } from '@js-app/entities/Bill'

export default {
  name: 'BillsTableItem',
  components: {
    ShareBillLinkPopup,
    RequireAccess,
    TransactionReceiptPopup,
    TransactionStatus,
    TransactionTableItem,
  },
  props: {
    transaction: {
      type: Transaction,
    },
  },
  data: () => ({
    transactionReceiptBlob: null,
    bill: null,
  }),

  created() {
    this.bill = new Bill(this.transaction.transactionData)
  },

  computed: {
    ...mapState(['selectedCompany']),
    itemActions() {
      let _actions = []

      if (this.transaction.canGetReceipt) {
        _actions.push({
          id: 'get-receipt',
          title: this.$t('transaction-actions.get-receipt'),
          handler: this.loadReceipt,
        })
      }

      if (this.transaction.canMakeRefund || this.transaction.canCancelPreAuth) {
        _actions.push({
          id: 'make-refund',
          title: this.$t('transaction-actions.make-refund'),
          handler: this.toBillRefund,
          accessControl: {
            action: 'read',
            data: 'bills-refund',
          },
        })
      }

      _actions.push({
        id: 'bill-details',
        title: this.$t('transaction-actions.bill-details'),
        handler: this.toBillDetails,
      })

      _actions.push({
        id: 'bill-share',
        title: this.$t('transaction-actions.bill-share'),
        handler: this.shareBill,
      })

      _actions.push({
        id: 'support-tg-chat',
        title: this.$t('transaction-actions.support-tg-chat'),
        handler: SupportService.goToTelegramChat,
      })

      return _actions
    },
  },

  refundOperationId: REFUND,

  methods: {
    async handleActionFired(action) {
      try {
        await action.handler()
      } catch (e) {
        console.error(e)
      }

      try {
        this.$refs.transactionItem.actionFinished(action.id)
      } catch (e) {}
    },
    async toBillDetails() {
      await this.$router.push(
        RouteHelper.getRoute('bill-view', {
          companyId: this.selectedCompany,
          billId: this.transaction.operationId,
        })
      )
    },
    async toBillRefund() {
      await this.$router.push(
        RouteHelper.getRoute('bill-refund', {
          companyId: this.selectedCompany,
          billId: this.transaction.operationId,
        })
      )
    },
    async loadReceipt() {
      let receiptResponse = await CompaniesApi.getBillReceipt(
        this.selectedCompany,
        this.transaction.operationId
      )
      this.transactionReceiptBlob = URL.createObjectURL(receiptResponse.data)
      return this.$refs.transactionReceiptPopup.open()
    },
    async shareBill() {
      this.bill = new Bill(await this.$refs.transactionItem.loadFullInfo())
      this.$refs.shareBillLinkPopup.open(this.bill)
    },
  },
}
</script>

<style lang="scss" scoped>
.bills-table-item {
  .transaction-table-item {
    &__phone-email {
      word-break: break-all;
    }

    &__type {
      display: flex;
      align-items: center;
      justify-content: center;

      position: relative;

      &-refund {
        position: absolute;
        left: 0;
        top: 50%;
        transform: translateY(-50%);
        display: flex;
        align-items: center;
        justify-content: center;
      }
    }

    &__id {
      word-break: break-all;
    }

    &__amount {
      font-weight: 600;
      font-size: 14px;
      line-height: 18px;
      color: #1a1f29;
      opacity: 0.7;
      justify-content: flex-end;
    }

    &__commission {
      word-break: break-all;
      font-weight: 500;
      font-size: 12px;
      line-height: 16px;
      color: rgba(#585f6d, 0.7);
    }

    &__recipient {
      font-weight: 500;
      font-size: 14px;
      line-height: 20px;
      color: #1a1f29;
      opacity: 0.7;
      overflow-wrap: anywhere;
    }

    &__currency {
      font-weight: 500;
      font-size: 12px;
      line-height: 16px;
      color: rgba(#585f6d, 0.7);
    }

    &__comment {
      font-weight: 500;
      font-size: 12px;
      line-height: 16px;
      color: rgba(#585f6d, 0.7);
    }

    &__status {
      align-self: center;
      display: flex;
      align-items: center;
      justify-content: center;
      padding-left: 20px;
      padding-right: 20px;

      &-item {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 105px;
        max-width: 100%;
        height: 28px;
        border-radius: 6px;
        font-weight: 500;
        font-size: 12px;
        line-height: 28px;

        &--paid {
          background: rgba(39, 204, 36, 0.2);
          color: #44d941;
        }
      }
    }

    &__number {
      word-break: break-all;
    }
  }
}
</style>
